import { parseVertexCredentialV814 } from './vertexCredentialsV814';

export const VERTEX_MAX_CONCURRENCY_V815 = 10000;
export const VERTEX_DEFAULT_LLM_CONCURRENCY_V815 = 0;
export const VERTEX_DEFAULT_IMAGE_CONCURRENCY_V815 = 2;

export const normalizeVertexConcurrencyLimitV815 = (value, fallback, label) => {
  const normalized =
    value === '' || value === null || value === undefined ? fallback : value;
  const parsed = Number(normalized);
  if (
    !Number.isInteger(parsed) ||
    parsed < 0 ||
    parsed > VERTEX_MAX_CONCURRENCY_V815
  ) {
    throw new Error(
      `${label} must be an integer between 0 and ${VERTEX_MAX_CONCURRENCY_V815}.`
    );
  }
  return parsed;
};

const buildMaskedKeyConfigV816 = (row, index, label) => {
  const id = Number.parseInt(String(row?.id || ''), 10);
  if (!Number.isInteger(id) || id <= 0) {
    throw new Error(`${label} #${index + 1} is missing its saved key ID.`);
  }
  return {
    id,
    ...(row?.quota_group_v95 !== undefined
      ? { quota_group_v95: String(row.quota_group_v95).trim() }
      : {}),
    key_value: '',
    llm_concurrency_limit: normalizeVertexConcurrencyLimitV815(
      row?.llm_concurrency_limit,
      VERTEX_DEFAULT_LLM_CONCURRENCY_V815,
      `${label} #${index + 1} LLM concurrency`
    ),
    image_concurrency_limit: normalizeVertexConcurrencyLimitV815(
      row?.image_concurrency_limit,
      VERTEX_DEFAULT_IMAGE_CONCURRENCY_V815,
      `${label} #${index + 1} image concurrency`
    ),
  };
};

export const buildVertexKeyConfigsV815 = (rows = []) => {
  const configs = [];
  const seenProjects = new Set();
  const seenMaskedIds = new Set();
  rows.forEach((row, index) => {
    if (row?.key_masked) {
      const config = buildMaskedKeyConfigV816(row, index, 'Vertex credential');
      if (!seenMaskedIds.has(config.id)) {
        seenMaskedIds.add(config.id);
        configs.push(config);
      }
      return;
    }
    const rawValue = String(row?.key_value || '').trim();
    if (!rawValue) return;
    const { projectId, keyValue } = parseVertexCredentialV814(rawValue);
    if (seenProjects.has(projectId)) {
      throw new Error(
        `Vertex AI project ${projectId} can only appear once in a channel.`
      );
    }
    seenProjects.add(projectId);
    const id = Number.parseInt(String(row?.id || ''), 10);
    const config = {
      key_value: keyValue,
      ...(row?.quota_group_v95 !== undefined
        ? { quota_group_v95: String(row.quota_group_v95).trim() }
        : {}),
      llm_concurrency_limit: normalizeVertexConcurrencyLimitV815(
        row?.llm_concurrency_limit,
        VERTEX_DEFAULT_LLM_CONCURRENCY_V815,
        `Vertex credential #${index + 1} LLM concurrency`
      ),
      image_concurrency_limit: normalizeVertexConcurrencyLimitV815(
        row?.image_concurrency_limit,
        VERTEX_DEFAULT_IMAGE_CONCURRENCY_V815,
        `Vertex credential #${index + 1} image concurrency`
      ),
    };
    if (Number.isInteger(id) && id > 0) config.id = id;
    configs.push(config);
  });
  return configs;
};

export const buildGeminiEnterpriseKeyConfigsV815 = (rows = []) => {
  const configs = [];
  const seenKeys = new Set();
  const seenMaskedIds = new Set();
  rows.forEach((row, index) => {
    if (row?.key_masked) {
      const config = buildMaskedKeyConfigV816(
        row,
        index,
        'Gemini Enterprise API key'
      );
      if (!seenMaskedIds.has(config.id)) {
        seenMaskedIds.add(config.id);
        configs.push(config);
      }
      return;
    }
    const keyValue = String(row?.key_value || '').trim();
    if (!keyValue || seenKeys.has(keyValue)) return;
    if (/\s/.test(keyValue)) {
      throw new Error(
        `Gemini Enterprise API key #${index + 1} must not contain whitespace.`
      );
    }
    seenKeys.add(keyValue);
    const id = Number.parseInt(String(row?.id || ''), 10);
    const config = {
      key_value: keyValue,
      ...(row?.quota_group_v95 !== undefined
        ? { quota_group_v95: String(row.quota_group_v95).trim() }
        : {}),
      llm_concurrency_limit: normalizeVertexConcurrencyLimitV815(
        row?.llm_concurrency_limit,
        VERTEX_DEFAULT_LLM_CONCURRENCY_V815,
        `Gemini Enterprise API key #${index + 1} LLM concurrency`
      ),
      image_concurrency_limit: normalizeVertexConcurrencyLimitV815(
        row?.image_concurrency_limit,
        VERTEX_DEFAULT_IMAGE_CONCURRENCY_V815,
        `Gemini Enterprise API key #${index + 1} image concurrency`
      ),
    };
    if (Number.isInteger(id) && id > 0) config.id = id;
    configs.push(config);
  });
  return configs;
};

export const buildChannelKeySubmissionV816 = (channelType, rows = []) => {
  const keyConfigs =
    channelType === 42
      ? buildVertexKeyConfigsV815(rows)
      : buildGeminiEnterpriseKeyConfigsV815(rows);
  return {
    keyConfigs,
    keyValues: keyConfigs
      .map((config) => String(config.key_value || '').trim())
      .filter(Boolean),
  };
};
